/**
 * Platform domains — FALLBACK only (prefer cluster hostnames from API).
 * Set REACT_APP_PLATFORM_DOMAINS to override.
 */
const PLATFORM_DOMAINS = (process.env.REACT_APP_PLATFORM_DOMAINS || 'vercel.app,netlify.app,onrender.com,herokuapp.com,pages.dev')
  .split(',')
  .map(d => d.trim())
  .filter(Boolean);

/** Cluster hostnames loaded from server on app init. */
let clusterHostnames = [];

/** Tenant base domains loaded from server on app init. */
let clusterTenantBaseDomains = [];

/** Called once from App.js after fetching GET /api/v1/cluster/config */
export const setClusterHostnames = (hostnames, tenantBaseDomains) => {
  clusterHostnames = hostnames || [];
  clusterTenantBaseDomains = tenantBaseDomains || [];
};

export const getClusterHostnames = () => clusterHostnames;

const findPlatformDomain = (hostname) =>
  PLATFORM_DOMAINS.find(d => hostname.endsWith('.' + d) || hostname === d);

/**
 * Get the number of parts that make up the "root" hostname (no subdomain).
 * Checks cluster cache first, then PLATFORM_DOMAINS fallback, then default 2.
 */
const getRootPartCount = (hostname) => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 1;
  if (hostname.endsWith('.localhost')) return 2;

  // Cluster cache is handled separately in getSubdomain/getBaseDomain
  // This function is only the PLATFORM_DOMAINS + default fallback
  const platform = findPlatformDomain(hostname);
  if (platform) {
    return platform.split('.').length + 1;
  }

  return 2;
};

/**
 * Check if hostname matches a cluster root or tenant_base_domain exactly.
 */
const isClusterRoot = (hostname) =>
  clusterHostnames.includes(hostname) || clusterTenantBaseDomains.includes(hostname);

/**
 * If hostname ends with `.{cluster}` or `.{tenantBaseDomain}`, return the subdomain portion.
 * Returns null if no cluster match.
 */
const extractSubdomainFromClusters = (hostname) => {
  // Check cluster hostnames
  for (const cluster of clusterHostnames) {
    const suffix = '.' + cluster;
    if (hostname.endsWith(suffix)) {
      const sub = hostname.slice(0, hostname.length - suffix.length);
      return sub || null;
    }
  }
  // Check tenant base domains
  for (const domain of clusterTenantBaseDomains) {
    const suffix = '.' + domain;
    if (hostname.endsWith(suffix)) {
      const sub = hostname.slice(0, hostname.length - suffix.length);
      return sub || null;
    }
  }
  return null;
};

/**
 * Get the current subdomain from the browser URL.
 * Returns null if on root domain.
 *
 * Priority:
 *   1. localhost / 127.0.0.1 → null
 *   2. *.localhost → extract before .localhost
 *   3. Cluster cache — exact match → null (root); endsWith .{cluster} or .{tenantBaseDomain} → subdomain
 *   4. PLATFORM_DOMAINS fallback + default 2-part root
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, hostname.indexOf('.localhost'));
    return sub || null;
  }

  // Cluster cache
  if (isClusterRoot(hostname)) return null;
  const clusterSub = extractSubdomainFromClusters(hostname);
  if (clusterSub) return clusterSub;

  // PLATFORM_DOMAINS fallback + default
  const rootParts = getRootPartCount(hostname);
  const hostParts = hostname.split('.');

  if (hostParts.length > rootParts) {
    return hostParts[0];
  }

  return null;
};

export const isSubdomain = () => getSubdomain() !== null;
export const isRootDomain = () => getSubdomain() === null;

/**
 * Get the "base" hostname (root without any subdomain).
 * When on a tenant_base_domain, returns that as the base for building subdomain URLs.
 */
export const getBaseDomain = () => {
  const { hostname, port, protocol } = window.location;

  let baseHostname = hostname;

  if (hostname.endsWith('.localhost')) {
    baseHostname = 'localhost';
  } else {
    // Cluster cache: find matching cluster root or tenant_base_domain
    if (isClusterRoot(hostname)) {
      // If hostname is a cluster hostname, use its tenant_base_domain if available
      // (e.g. on wapingo.mydomain.com, base should be mydomain.com for tenant URLs)
      const tbd = findTenantBaseDomainForHostname(hostname);
      baseHostname = tbd || hostname;
    } else {
      const clusterSub = extractSubdomainFromClusters(hostname);
      if (clusterSub) {
        // hostname = "acme.mydomain.com" or "acme.cluster.example.com", strip "acme."
        baseHostname = hostname.slice(clusterSub.length + 1);
      } else {
        // PLATFORM_DOMAINS fallback
        const rootParts = getRootPartCount(hostname);
        const parts = hostname.split('.');
        if (parts.length > rootParts) {
          baseHostname = parts.slice(parts.length - rootParts).join('.');
        }
      }
    }
  }

  const portStr = port ? ':' + port : '';
  return { base: baseHostname + portStr, protocol };
};

/**
 * For a given hostname that is a cluster root, find its tenant_base_domain.
 * Since the client only has flat arrays, we rely on the server pairing:
 * if hostname is in clusterHostnames, check if there's a tenantBaseDomain
 * that this hostname "contains" (i.e. hostname ends with tenantBaseDomain).
 * Returns the tenant_base_domain or null.
 */
const findTenantBaseDomainForHostname = (hostname) => {
  // If hostname itself is a tenant_base_domain, return it as-is
  if (clusterTenantBaseDomains.includes(hostname)) return hostname;
  // If hostname is a cluster hostname, check if it ends with any tenant_base_domain
  // e.g. "wapingo.mydomain.com" ends with "mydomain.com"
  for (const tbd of clusterTenantBaseDomains) {
    if (hostname.endsWith('.' + tbd)) return tbd;
  }
  return null;
};

/**
 * Build a full URL for a given subdomain + path.
 */
export const buildSubdomainUrl = (subdomain, path = '/') => {
  if (!subdomain) return null;
  const { base, protocol } = getBaseDomain();
  return `${protocol}//${subdomain}.${base}${path}`;
};

/**
 * Suffix shown in UI for subdomain input fields.
 */
export const getSubdomainSuffix = () => {
  const { base } = getBaseDomain();
  return `.${base}`;
};
